import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent } from './ui/dialog';
import StandardModal from './StandardModal';
import { PrimaryButton, SecondaryButton, SuccessButton } from './Buttons';
import RoleSelector from './RoleSelector';
import { useRoles } from '../hooks/useData';
import useModalPersistence from '../hooks/useModalPersistence';
import { 
  ChevronLeft, 
  ChevronRight, 
  Check, 
  Plus, 
  User, 
  Users, 
  Key, 
  Calendar, 
  Settings,
  Eye,
  X,
  Loader2,
  Clock,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import ScheduleEditor from './ScheduleEditor';

const AddTeamModal = ({ isOpen, onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const { roles } = useRoles(); // Use useSWR instead of manual fetching
  const [usernameAvailable, setUsernameAvailable] = useState(null); // null, true, false
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null); // null, true, false
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [phoneAvailable, setPhoneAvailable] = useState(null); // null, true, false
  const [checkingPhone, setCheckingPhone] = useState(false);
  
  // Modal persistence
  const initialData = {
    name: '',
    email: '',
    phone: '',
    roleId: '',
    roleName: '',
    username: '',
    password: '',
    doubleDeckerCertified: false,
    recurringAvailability: [],
    unavailableDates: [],
    schedulerWeight: 3
  };
  
  const { formData, setFormData, clearDraft } = useModalPersistence('draft_addTeam', initialData, isOpen);

  const totalSteps = 6;
  const DAYS_OF_WEEK = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  const [currentDay, setCurrentDay] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Reset to step 1 when modal opens
      setCurrentStep(1);
      
      // Re-validate email and phone if draft was restored
      if (formData.email && formData.email.includes('@')) {
        updateFormData('email', formData.email);
      }
      if (formData.phone) {
        updateFormData('phone', formData.phone);
      }
      if (formData.username) {
        updateFormData('username', formData.username);
      }
    }
  }, [isOpen]);

  const resetForm = () => {
    setCurrentStep(1);
    setFormData(initialData);
    clearDraft();
    setCurrentDay('');
    setStartTime('');
    setEndTime('');
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // If updating username, debounce check
    if (field === 'username' && value) {
      setUsernameAvailable(null);
      setCheckingUsername(true);
      
      // Clear previous timeout
      if (window.usernameCheckTimeout) {
        clearTimeout(window.usernameCheckTimeout);
      }
      
      // Debounce 500ms
      window.usernameCheckTimeout = setTimeout(async () => {
        try {
          const response = await api.get(`/team`);
          const teamMembers = response.data || [];
          const exists = teamMembers.some(c => c.username === value);
          setUsernameAvailable(!exists);
        } catch (error) {
          console.error('Error checking username:', error);
        } finally {
          setCheckingUsername(false);
        }
      }, 500);
    }
    
    // If updating email, debounce check
    if (field === 'email' && value && value.includes('@')) {
      setEmailAvailable(null);
      setCheckingEmail(true);
      
      if (window.emailCheckTimeout) {
        clearTimeout(window.emailCheckTimeout);
      }
      
      window.emailCheckTimeout = setTimeout(async () => {
        try {
          const response = await api.get(`/team-members/check-email/${encodeURIComponent(value)}`);
          setEmailAvailable(response.data.available);
        } catch (error) {
          console.error('Error checking email:', error);
          setEmailAvailable(true); // Fail-open
        } finally {
          setCheckingEmail(false);
        }
      }, 500);
    }
    
    // If updating phone, debounce check
    if (field === 'phone' && value) {
      setPhoneAvailable(null);
      setCheckingPhone(true);
      
      if (window.phoneCheckTimeout) {
        clearTimeout(window.phoneCheckTimeout);
      }
      
      window.phoneCheckTimeout = setTimeout(async () => {
        try {
          const response = await api.get(`/team-members/check-phone/${encodeURIComponent(value)}`);
          setPhoneAvailable(response.data.available);
        } catch (error) {
          console.error('Error checking phone:', error);
          setPhoneAvailable(true); // Fail-open
        } finally {
          setCheckingPhone(false);
        }
      }, 500);
    }
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.name.trim()) {
          toast.error('Please enter teamMember name');
          return false;
        }
        if (!formData.email.trim()) {
          toast.error('Please enter email address');
          return false;
        }
        if (!formData.email.includes('@')) {
          toast.error('Please enter a valid email');
          return false;
        }
        if (checkingEmail) {
          toast.error('Please wait while we verify email availability');
          return false;
        }
        if (emailAvailable !== true) {
          toast.error('Please use an available email address');
          return false;
        }
        if (!formData.phone.trim()) {
          toast.error('Please enter phone number');
          return false;
        }
        if (checkingPhone) {
          toast.error('Please wait while we verify phone availability');
          return false;
        }
        if (phoneAvailable === false) {
          toast.error('Phone already in use in this organization');
          return false;
        }
        return true;
      case 2:
        if (!formData.roleId) {
          toast.error('Please select a role');
          return false;
        }
        return true;
      case 3:
        if (!formData.username.trim()) {
          toast.error('Please enter username');
          return false;
        }
        if (usernameAvailable === false) {
          toast.error('Username already taken - please choose another');
          return false;
        }
        if (!formData.password) {
          toast.error('Please enter password');
          return false;
        }
        // Enforce strong password requirements
        if (formData.password.length < 8 ||
            !/[a-z]/.test(formData.password) ||
            !/[A-Z]/.test(formData.password) ||
            !/[0-9]/.test(formData.password) ||
            !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password)) {
          toast.error('Please meet all password requirements');
          return false;
        }
        return true;
      case 4:
        if (formData.recurringAvailability.length === 0) {
          toast.error('Please set availability schedule');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email?.trim() || null,
        phone: formData.phone?.trim() || null,
        username: formData.username.trim(),
        password: formData.password,
        roleId: formData.roleId,
        roleName: formData.roleName,
        recurringAvailability: formData.recurringAvailability,
        unavailableDates: formData.unavailableDates,
        payRate: 20.0,
        schedulerWeight: formData.schedulerWeight,
        doubleDeckerCertified: formData.doubleDeckerCertified
      };

      await api.post('/team', payload);
      toast.success(`🎉 ${formData.name} added successfully!`);
      clearDraft(); // Clear draft on success
      onSuccess(); // Refresh team list
      onClose(); // Close modal
    } catch (error) {
      console.error('Error adding teamMember:', error);
      
      let errorMessage = 'Failed to add teamMember';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          const validationErrors = error.response.data.detail.map(err => 
            `${err.loc?.[err.loc.length - 1] || 'Field'}: ${err.msg}`
          ).join(', ');
          errorMessage = `Validation errors: ${validationErrors}`;
        }
      }
      toast.error(errorMessage, { duration: 6000 });
    }
  };

  const generateUsername = () => {
    if (formData.name) {
      const username = formData.name.toLowerCase().replace(/[^a-z0-9]/g, '');
      updateFormData('username', username);
    }
  };

  const addAvailability = () => {
    if (currentDay && startTime && endTime) {
      const newAvailability = {
        day: currentDay,
        startTime: startTime,
        endTime: endTime
      };
      
      // Check for duplicates
      const exists = formData.recurringAvailability.find(a => a.day === currentDay);
      if (exists) {
        toast.error('Availability already set for this day');
        return;
      }
      
      updateFormData('recurringAvailability', [...formData.recurringAvailability, newAvailability]);
      setCurrentDay('');
      setStartTime('');
      setEndTime('');
      toast.success('Availability added');
    }
  };

  const removeAvailability = (index) => {
    const updated = formData.recurringAvailability.filter((_, i) => i !== index);
    updateFormData('recurringAvailability', updated);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Basic Information</h2>
              <p className="text-gray-600 text-sm mt-1">Let's start with the teamMember's details</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-gray-700">Full Name <span className="text-red-500">*</span></Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateFormData('name', e.target.value)}
                  placeholder="Enter teamMember's full name"
                  className="mt-1 h-11"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email Address <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    placeholder="team@example.com"
                    className="mt-1 h-11 pr-10"
                  />
                  {formData.email && formData.email.includes('@') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {checkingEmail ? (
                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                      ) : emailAvailable === true ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : emailAvailable === false ? (
                        <X className="w-4 h-4 text-red-600" />
                      ) : null}
                    </div>
                  )}
                </div>
                {emailAvailable === false && (
                  <p className="text-xs text-red-600 mt-1">Email already in use in this organization</p>
                )}
              </div>

              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Phone Number <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={formData.phone}
                    onChange={(e) => {
                      // Only allow numbers, spaces, hyphens, parentheses, plus
                      const cleaned = e.target.value.replace(/[^0-9\s\-\(\)\+]/g, '');
                      updateFormData('phone', cleaned);
                    }}
                    placeholder="(555) 123-4567"
                    className="mt-1 h-11 pr-10"
                  />
                  {formData.phone && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {checkingPhone ? (
                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                      ) : phoneAvailable === true ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : phoneAvailable === false ? (
                        <X className="w-4 h-4 text-red-600" />
                      ) : null}
                    </div>
                  )}
                </div>
                {phoneAvailable === false && (
                  <p className="text-xs text-red-600 mt-1">Phone already in use in this organization</p>
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Assign Role</h2>
              <p className="text-gray-600 text-sm mt-1">What role will {formData.name || 'this teamMember'} have?</p>
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Select Role <span className="text-red-500">*</span></Label>
              <RoleSelector
                roles={roles}
                value={formData.roleId}
                onChange={(roleId) => {
                  const role = roles.find(r => r.id === roleId);
                  updateFormData('roleId', roleId);
                  updateFormData('roleName', role?.name || '');
                }}
                placeholder="Choose a role..."
                showAddButton={true}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Key className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Login Credentials</h2>
              <p className="text-gray-600 text-sm mt-1">Set up login access for {formData.name || 'this teamMember'}</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="username" className="text-sm font-medium text-gray-700">Username <span className="text-red-500">*</span></Label>
                <div className="flex gap-2 mt-1">
                  <div className="relative flex-1">
                    <Input
                      id="username"
                      value={formData.username}
                      onChange={(e) => {
                        // Force lowercase, no spaces, only alphanumeric
                        const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '');
                        updateFormData('username', cleaned);
                      }}
                      placeholder="username"
                      className={`h-11 pr-10 ${
                        usernameAvailable === false ? 'border-red-300' : 
                        usernameAvailable === true ? 'border-green-300' : ''
                      }`}
                    />
                    {/* Validation Feedback - positioned inside input on the right */}
                    {formData.username && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
                        {checkingUsername ? (
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        ) : usernameAvailable === true ? (
                          <Check className="w-5 h-5 text-green-600" />
                        ) : usernameAvailable === false ? (
                          <X className="w-5 h-5 text-red-600" />
                        ) : null}
                      </div>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={generateUsername}
                    className="px-3 h-11 text-xs flex-shrink-0"
                  >
                    Auto
                  </Button>
                </div>
                {usernameAvailable === false && (
                  <p className="text-xs text-red-600 mt-1">Username already taken</p>
                )}
                {usernameAvailable === true && (
                  <p className="text-xs text-green-600 mt-1">Username available</p>
                )}
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => updateFormData('password', e.target.value)}
                  placeholder="Create a strong password"
                  className="mt-1 h-11"
                />
              </div>

              {/* Password Requirements Checklist */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <p className="text-xs font-semibold text-gray-700 mb-2">Password Requirements:</p>
                
                <div className="flex items-center gap-2">
                  {formData.password.length >= 8 ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                  <span className={`text-xs ${formData.password.length >= 8 ? 'text-green-700' : 'text-gray-600'}`}>8+ characters</span>
                </div>

                <div className="flex items-center gap-2">
                  {/[a-z]/.test(formData.password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                  <span className={`text-xs ${/[a-z]/.test(formData.password) ? 'text-green-700' : 'text-gray-600'}`}>1 lowercase letter</span>
                </div>

                <div className="flex items-center gap-2">
                  {/[A-Z]/.test(formData.password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                  <span className={`text-xs ${/[A-Z]/.test(formData.password) ? 'text-green-700' : 'text-gray-600'}`}>1 uppercase letter</span>
                </div>

                <div className="flex items-center gap-2">
                  {/[0-9]/.test(formData.password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                  <span className={`text-xs ${/[0-9]/.test(formData.password) ? 'text-green-700' : 'text-gray-600'}`}>1 number</span>
                </div>

                <div className="flex items-center gap-2">
                  {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                  <span className={`text-xs ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password) ? 'text-green-700' : 'text-gray-600'}`}>1 symbol (!@#$%^&*)</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Calendar className="w-6 h-6 text-orange-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Weekly Schedule</h2>
              <p className="text-gray-600 text-sm mt-1">Set {formData.name}'s typical availability</p>
            </div>

            <ScheduleEditor
              value={formData.recurringAvailability}
              onChange={(newSchedule) => updateFormData('recurringAvailability', newSchedule)}
            />
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Zap className="w-6 h-6 text-yellow-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Priority</h2>
              <p className="text-gray-600 text-sm mt-1">How often should {formData.name || 'this teamMember'} be auto-scheduled?</p>
            </div>

            <div className="space-y-3">
              {[
                { value: 5, label: 'High', desc: 'Schedule frequently' },
                { value: 3, label: 'Normal', desc: 'Standard frequency' },
                { value: 1, label: 'Low', desc: 'Schedule less often' }
              ].map((option) => (
                <div
                  key={option.value}
                  onClick={() => updateFormData('schedulerWeight', option.value)}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.schedulerWeight === option.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{option.label}</div>
                      <p className="text-xs text-gray-600 mt-0.5">{option.desc}</p>
                    </div>
                    {formData.schedulerWeight === option.value && (
                      <Check className="w-4 h-4 text-blue-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Eye className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Review & Confirm</h2>
              <p className="text-gray-600 text-sm mt-1">Please review the details before adding {formData.name || 'this teamMember'}</p>
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {/* Basic Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Basic Information
                </h3>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Name:</span> {formData.name}</p>
                  {formData.email && <p><span className="font-medium">Email:</span> {formData.email}</p>}
                  {formData.phone && <p><span className="font-medium">Phone:</span> {formData.phone}</p>}
                </div>
              </div>

              {/* Role */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Role
                </h3>
                <Badge style={{ backgroundColor: roles.find(r => r.id === formData.roleId)?.color || '#3B82F6' }}>
                  {formData.roleName}
                </Badge>
              </div>

              {/* Credentials */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Login Credentials
                </h3>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Username:</span> {formData.username}</p>
                  <p><span className="font-medium">Password:</span> ••••••••</p>
                </div>
              </div>

              {/* Availability */}
              {formData.recurringAvailability.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Weekly Availability
                  </h3>
                  <div className="space-y-1 text-sm">
                    {formData.recurringAvailability.map((avail, idx) => (
                      <p key={idx}>
                        <span className="font-medium capitalize">{avail.day}:</span> {avail.startTime} - {avail.endTime}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Scheduler Weight */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Priority
                </h3>
                <span className="text-sm">
                  {formData.schedulerWeight === 5 ? 'High' :
                   formData.schedulerWeight === 3 ? 'Normal' : 'Low'}
                </span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch(currentStep) {
      case 1: return 'Basic Info';
      case 2: return 'Assign Role';
      case 3: return 'Login Credentials';
      case 4: return 'Availability';
      case 5: return 'Priority';
      case 6: return 'Review';
      default: return '';
    }
  };

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Team"
      description={null}
      size="md"
      showCloseButton={false}
      footer={null}
    >
      {/* Visual Progress Bar */}
      <div className="relative h-2 bg-gray-200 rounded-full mb-8">
        <div 
          style={{ backgroundColor: '#2563EB' }}
          className="absolute h-full rounded-full transition-all duration-300"
          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
        />
      </div>

      {/* Step Content */}
      <div className="min-h-[300px]">
        {renderStepContent()}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between gap-3 pt-4">
        {currentStep > 1 ? (
          <SecondaryButton onClick={prevStep}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </SecondaryButton>
        ) : (
          <SecondaryButton onClick={onClose}>
            Cancel
          </SecondaryButton>
        )}
        
        {currentStep === totalSteps ? (
          <PrimaryButton onClick={handleSubmit}>
            <Check className="w-4 h-4 mr-1" />
            Add Team
          </PrimaryButton>
        ) : (
          <PrimaryButton 
            onClick={nextStep}
            disabled={
              (currentStep === 1 && (
                !formData.name.trim() || 
                !formData.email.trim() || 
                !formData.phone.trim() ||
                checkingEmail || 
                emailAvailable !== true ||
                checkingPhone ||
                phoneAvailable !== true
              )) ||
              (currentStep === 3 && (
                !formData.username.trim() ||
                checkingUsername || 
                usernameAvailable !== true
              ))
            }
            className="disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </PrimaryButton>
        )}
      </div>
    </StandardModal>
  );
};

export default AddTeamModal;